import { authContext } from '../auth-context.ts';
import { generateTraceparent } from './traceparent.ts';

// tio-data deliberately doesn't depend on tio-common, so inline the
// same defaults the auth handler uses for the builder pipeline.
const defaultTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || '';
const defaultRequestId = () => crypto.randomUUID();

/**
 * Auth options for direct fetch downloads and presigned-S3 flows. Store requests receive auth
 * automatically.
 * Owner-free (ADR-001): token and auth-key providers are read from authContext at call time.
 */
export interface TioAuthHeadersOptions {
  apiKey: string;
  contentType?: string;
  accept?: string;
  jsonApiKeyFormat?: string;
  timeZone?: () => string;
  requestId?: () => string;
  traceparent?: () => string;
}

export function buildTioAuthHeaders(options: TioAuthHeadersOptions): Headers {
  const headers = new Headers();
  if (options.contentType) headers.set('Content-Type', options.contentType);
  if (options.accept) headers.set('Accept', options.accept);
  headers.set('x-api-key', options.apiKey);
  const authKey = authContext.publicKey();
  if (authKey) headers.set('x-auth-key', authKey);
  if (options.jsonApiKeyFormat) headers.set('x-json-api-key-format', options.jsonApiKeyFormat);
  headers.set('Time-Zone', (options.timeZone ?? defaultTimeZone)());
  headers.set('X-Request-Id', (options.requestId ?? defaultRequestId)());
  headers.set('traceparent', (options.traceparent ?? generateTraceparent)());

  // Mirror the auth handler: any real token is sent (the post-login boot
  // holds the token before `isAuthenticated` flips true — see
  // request-manager.ts), garbage/absent tokens are not.
  const accessToken = authContext.token();
  if (accessToken) {
    headers.set('tio-auth-token', accessToken);
  }

  return headers;
}

/**
 * Same auth-header bundle as {@link buildTioAuthHeaders}, but as a plain
 * `Record<string, string>` instead of a `Headers` object.
 *
 * Some consumers can't take a `Headers` object — notably tio-ui's
 * `<DownloadButton @authHeaders={{...}} />`, whose `authHeaders` arg is typed
 * `() => Record<string, string>` (see `addons/tio-ui/src/components/download-button.gts`).
 * The flagged admin call sites that fed it `adapterFor('application').headers`
 * (a plain Record) — e.g. `accounting/invoices/show.gts#downloadHeaders` — swap to
 * this helper in 3.2c-ii. (The `company-settings/data-transfer-agent.gts` +
 * `profile/payments.gts` call sites already do `Object.fromEntries(
 * buildTioAuthHeaders(...).entries())` inline; this formalizes that one-liner.)
 *
 * It delegates to {@link buildTioAuthHeaders} and flattens the result, so the
 * key set + values are GUARANTEED identical (incl. the vnd.api+json
 * Content-Type / Accept when those options are supplied). Note: `Headers`
 * lowercases its keys, so the emitted Record has lowercase keys (`content-type`,
 * `x-api-key`, etc.) — exactly what the inline `Object.fromEntries(...entries())`
 * call sites already produce.
 */
export function buildTioAuthHeadersRecord(options: TioAuthHeadersOptions): Record<string, string> {
  return Object.fromEntries(buildTioAuthHeaders(options).entries());
}
